/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  errorEditOptions,
  successAddOptions,
  successEditOptions as bulkSuccessEditOptions,
  successEditOptions
} from '@ion/applications/src/components/prompts/constants';
import {getAccountId} from '@ion/applications/src/helpers';
import {importConfirm} from '@ion/applications/src/store/actions/importer';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {normalizeAsset} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  consolidatedAssetUpdate,
  createAsset,
  createAssetDevices,
  deleteAsset as destroyAsset,
  deleteAssetDevices,
  deleteAssets as destroyAssets,
  deleteAssetSchedule,
  disassociateDevices,
  getAsset,
  loadAssets,
  manageAssetSchedules,
  updateAsset,
  updateAssetSchedule,
  updateAssetSchedules,
  updateAssetToolAssociations,
  updateDevice
} from '@ion/core/src/services/Connect';
import {entityId, hasSameAccount} from '@ion/core/src/services/ConnectUtils';
import {
  assignSchedule,
  deleteAssetMaintenanceSchedule
} from '@ion/core/src/services/Maintenance';
import {
  Asset,
  AssetsFilters,
  AuditChange,
  Device,
  Entity,
  Link,
  UpdateAssetAction
} from '@ion/core/src/types/entities';
import {
  GenericFunction
} from '@ion/core/src/types/general';
import {
  LoadAssetsParams,
  ServiceError
} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {PageState} from '@/types/pages';
import {
  AdminAction,
  AsyncState,
  PageInitializeAction
} from '@/types/redux';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../../../store/helpers';
import {gridDefaultPageSize} from '../../../utils/helpers';
import {determineAssetError} from './helpers';

// name
export const NAME                         = 'adminAssets';

// initialize assets ...
export const INITIALIZE_ADMIN_ASSETS      = 'INITIALIZE_ADMIN_ASSETS';

// refresh grid ...
export const ADMIN_ASSETS_GRID_REFRESHED  = 'ADMIN_ASSETS_GRID_REFRESHED';

export const SET_GRID_COLUMNS             = 'SET_ADMIN_ASSETS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS          = 'UPDATE_ADMIN_ASSETS_GRID_COLUMNS';

// grid sub groups
export const SET_VIEW_SUB_GROUPS          = 'SET_ADMIN_ASSETS_VIEW_SUB_GROUPS';

export const SET_GRID_PAGE_SIZE           = 'SET_ADMIN_ASSETS_GRID_PAGE_SIZE';

// retrieve assets ...
export const GET_ADMIN_ASSETS_REQUEST     = 'GET_ADMIN_ASSETS_REQUEST';
export const GET_ADMIN_ASSETS_RESPONSE    = 'GET_ADMIN_ASSETS_RESPONSE';
export const GET_ADMIN_ASSETS_ERROR       = 'GET_ADMIN_ASSETS_ERROR';

// asset add ...
export const ADD_ADMIN_ASSET              = 'ADD_ADMIN_ASSET';
export const ADD_ADMIN_ASSET_REQUEST      = 'ADD_ADMIN_ASSET_REQUEST';
export const ADD_ADMIN_ASSET_RESPONSE     = 'ADD_ADMIN_ASSET_RESPONSE';
export const ADD_ADMIN_ASSET_ERROR        = 'ADD_ADMIN_ASSET_ERROR';

// asset bulk import
export const IMPORT_ADMIN_ASSETS          = 'IMPORT_ADMIN_ASSETS';

// asset edit ...
export const EDIT_ADMIN_ASSET             = 'EDIT_ADMIN_ASSET';
export const EDIT_ADMIN_ASSET_REQUEST     = 'EDIT_ADMIN_ASSET_REQUEST';
export const EDIT_ADMIN_ASSET_RESPONSE    = 'EDIT_ADMIN_ASSET_RESPONSE';
export const EDIT_ADMIN_ASSET_ERROR       = 'EDIT_ADMIN_ASSET_ERROR';

// manage asset associations ...
export const MANAGE_ADMIN_ASSET           = 'MANAGE_ADMIN_ASSET';
export const MANAGE_ADMIN_ASSET_REQUEST   = 'MANAGE_ADMIN_ASSET_REQUEST';
export const MANAGE_ADMIN_ASSET_RESPONSE  = 'MANAGE_ADMIN_ASSET_RESPONSE';
export const MANAGE_ADMIN_ASSET_ERROR     = 'MANAGE_ADMIN_ASSET_ERROR';

// update asset associations ...
export const UPDATE_ADMIN_ASSET           = 'UPDATE_ADMIN_ASSET';
export const UPDATE_ADMIN_ASSET_REQUEST   = 'UPDATE_ADMIN_ASSET_REQUEST';
export const UPDATE_ADMIN_ASSET_RESPONSE  = 'UPDATE_ADMIN_ASSET_RESPONSE';
export const UPDATE_ADMIN_ASSET_ERROR     = 'UPDATE_ADMIN_ASSET_ERROR';

// view asset audit log ...
export const AUDIT_ADMIN_ASSET                     = 'AUDIT_ADMIN_ASSET';
export const AUDIT_ADMIN_ASSET_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_ASSET_SHOW_CHANGES_DIALOG';

// asset delete ...
export const DELETE_ADMIN_ASSET           = 'DELETE_ADMIN_ASSET';
export const DELETE_ADMIN_ASSET_REQUEST   = 'DELETE_ADMIN_ASSET_REQUEST';
export const DELETE_ADMIN_ASSET_RESPONSE  = 'DELETE_ADMIN_ASSET_RESPONSE';
export const DELETE_ADMIN_ASSET_ERROR     = 'DELETE_ADMIN_ASSET_ERROR';

// bulk asset edits ...
export const EDIT_ADMIN_ASSETS            = 'EDIT_ADMIN_ASSETS';
export const EDIT_ADMIN_ASSETS_REQUEST    = 'EDIT_ADMIN_ASSETS_REQUEST';
export const EDIT_ADMIN_ASSETS_RESPONSE   = 'EDIT_ADMIN_ASSETS_RESPONSE';
export const EDIT_ADMIN_ASSETS_ERROR      = 'EDIT_ADMIN_ASSETS_ERROR';

// bulk asset edits ...
export const MOVE_ADMIN_ASSETS            = 'MOVE_ADMIN_ASSETS';
export const MOVE_ADMIN_ASSETS_REQUEST    = 'MOVE_ADMIN_ASSETS_REQUEST';
export const MOVE_ADMIN_ASSETS_RESPONSE   = 'MOVE_ADMIN_ASSETS_RESPONSE';
export const MOVE_ADMIN_ASSETS_ERROR      = 'MOVE_ADMIN_ASSETS_ERROR';
export const SHOW_MOVED_ASSETS            = 'SHOW_MOVED_ASSETS';

//asset bulk edit group
export const ASSET_BULK_EDIT_GROUP        = 'ASSET_BULK_EDIT_GROUP';

// bulk asset deletes ...
export const DELETE_ADMIN_ASSETS          = 'DELETE_ADMIN_ASSETS';
export const DELETE_ADMIN_ASSETS_REQUEST  = 'DELETE_ADMIN_ASSETS_REQUEST';
export const DELETE_ADMIN_ASSETS_RESPONSE = 'DELETE_ADMIN_ASSETS_RESPONSE';
export const DELETE_ADMIN_ASSETS_ERROR    = 'DELETE_ADMIN_ASSETS_ERROR';

//Modular Search
export const UPDATE_ASSETS_SEARCH_QUERIES = 'UPDATE_ASSETS_SEARCH_QUERIES';
export const RESET_ASSETS_SEARCH_QUERIES  = 'RESET_ASSETS_SEARCH_QUERIES';

export const initializeAdminAssets = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_ASSETS};
};

export const adminAssetsGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_ASSETS_GRID_REFRESHED};
};

/**
 *
 * fetches assets for this account to be displayed in the admin
 * section
 */
export const getAdminAssets = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  showTimeoutError = false,
  includeSubGroups = false,
  includeAccountFilter = true,
  includeGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_ASSETS_REQUEST});
    const {
      adminAssets: {
        search
      }
    } = getState();
    const data: LoadAssetsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups,
      includeAccountFilter,
      includeGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    const modularSearchFilterOn = createModularSearchFilters(search);
    if (modularSearchFilterOn.groups === -1) {
      modularSearchFilterOn.groups = null;
    }
    data.filterOn = {
      ...data.filterOn,
      ...(includeAccountFilter && modularSearchFilterOn)
    };
    try {
      const {assets: items, totalCount} = await loadAssets(data);
      dispatch({type: GET_ADMIN_ASSETS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      const {errorCode, message, isCritical} = err;
      dispatch({type: GET_ADMIN_ASSETS_ERROR, error: err});
      callbackFn?.({error: err, items: [], totalCount: 0});
      if (showTimeoutError && err?.isCritical) {
        dispatch(toggleUserPrompt({
          message     : 'notifications:error.timeout',
          actionLabel : 'common:ok',
          autoHide    : false
        }));
      }
      if (showTimeoutError && errorCode === 400) {
        dispatch(toggleUserPrompt({
          message     : message || 'notifications:error.server',
          actionLabel : 'common:ok',
          autoHide    : true
        }));
      }
      return error(err);
      // [ION-SUITE TODO]: display error snackbar if this fails
    }
  };
};

/**
 * Given a Device, moves the device from it's current account to the Asset's account.
 * If the device already resides in the same account as that of the provided Asset,
 * the promise immediately resolves.
 */
export const verifyDevice = async(asset: Asset): Promise<Device | boolean> => {
  const {device} = asset;
  if (device?.account && !hasSameAccount(asset, device)) {
    device.account = asset.account;
    asset.updateAccount = true;
    delete asset.device;
    const updated = await updateDevice(device);
    return updated;
  }
  return Promise.resolve(true);
};

/**
 * Creates the Device associations for a newly created Asset.
 */
export const addAssetDevices = async(asset: Asset, devices?: Link[]): Promise<Asset> => {
  if (devices?.length) {
    const updated = await createAssetDevices(asset.id, devices);
    asset.devices = updated;
  }
  return asset;
};

/**
 *
 * Invoked when the Add Admin Asset editor is to be displayed or removed.
 */
export const addAdminAsset = (show = true) => ({
  type : ADD_ADMIN_ASSET, show
});

/**
 *
 * Invoked when the Add Admin Asset editor is to be displayed or removed.
 */
export const importAdminAssets = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.asset));
    dispatch({type: IMPORT_ADMIN_ASSETS, show, result});
  };
};

/**
 *
 * Invoked when a new asset is to be created; consolidating the process of Asset
 * creation into a single transaction. The process for new Asset creation is as
 * outlined below. Of the 6 potential async calls, 4 are blocking (see awaits).
 *
 * 1.) If a Device is to be associated with the new Asset, and the device does
 *     not reside in the same account as the Asset, the Device will first be
 *     moved. This step is delegated to the verifyDevice function.
 *
 * 2.) The asset's attributes are normalized and then the Asset is created.
 *
 * 3.) If a Device is to be associated with the Asset (see step #1), the newly
 *     created Asset is updated with the Device.
 *     This step is delegated to the addAssetDevices function within this module.
 *
 * 4.) If a Schedule is to be associated with the Asset, the Schedule is added.
 *
 * 5.) The MicroServices are refreshed to inform the API of the changes.
 *
 * 6.) If a Maintenance Schedule is to be associated with the Asset, it is added.
 *
 */
export const addAsset = (asset: Asset, showTreeRefresh: boolean = false): GenericFunction<any> => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_ASSET_REQUEST});
    const {schedule, maintenanceSchedule, device} = asset;
    try {
      await verifyDevice(asset);
      const devices = asset.devices || [];
      const created = await createAsset(normalizeAsset(asset));
      await addAssetDevices(created, devices);
      schedule && await manageAssetSchedules(created, [schedule]);
      maintenanceSchedule && assignSchedule(created, maintenanceSchedule);
      dispatch({type: ADD_ADMIN_ASSET_RESPONSE, asset: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.asset)));
    } catch (err) {
      const results = determineAssetError(err, device?.[0]?.name);
      dispatch({type: ADD_ADMIN_ASSET_ERROR, error: err});
      dispatch(toggleUserPrompt(results));
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Edit Admin Asset editor is to be displayed or removed.
 */
export const editAdminAsset = (asset: Asset, show = true, isView) => ({
  type : EDIT_ADMIN_ASSET, asset, show, isView
});

/**
 *
 * Invoked when an existing asset is to be updated; consolidating the process of
 * Asset updates into a single transaction. The process for new Asset creation is
 * outlined below.
 *
 * 1.) This step is delegated to the consolidatedAssetUpdate function of the connect
 *     services module, where the majority of complexity is managed, such as device
 *     associations, updating the asset, etc.
 *
 * 2.) If the Asset had a Maintenance Schedule, it will be removed.
 *
 * 3.) If the Asset has a Maintenance Schedule, it will be assigned.
 *
 */
export const editAsset = (
  asset: Asset,
  messageEntry?: string,
  updateAction?: UpdateAssetAction
): GenericFunction<any> => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ASSET_REQUEST});
    const {maintenanceSchedule, maintenanceSchedules, schedule, id, account, device} = asset;
    const executions = [];
    try {
      if (!maintenanceSchedule && maintenanceSchedules) {
        executions.unshift(deleteAssetMaintenanceSchedule(id));
      }
      const reloadAsset = await getAsset(asset?.id);
      const updated     = await consolidatedAssetUpdate({...asset, version: reloadAsset?.version});
      await updateAssetSchedule(asset);
      await addAssetDevices(asset, asset?.device);
      maintenanceSchedule && assignSchedule(asset, maintenanceSchedule);
      dispatch({type: EDIT_ADMIN_ASSET_RESPONSE, asset: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.asset, null, messageEntry)));
    } catch (err) {
      const results = determineAssetError(err, device?.[0]?.name, device);
      dispatch(toggleUserPrompt(results));
      dispatch({type: EDIT_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when an selected Asset is to be displayed, delegating to the getAsset
 * CTC API and dispatching a response of selected asset.
 */
export const viewAdminAsset = (assetId: number, show = true, isView) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ASSET_REQUEST});
    try {
      const result = await getAsset(assetId);
      dispatch({type: EDIT_ADMIN_ASSET_RESPONSE, asset: result});
      dispatch(editAdminAsset(result, show, isView));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Manage Admin Asset Associations editor is to be displayed
 * or removed.
 */
export const manageAdminAsset = (asset: Asset, show = true) => ({
  type : MANAGE_ADMIN_ASSET, asset, show
});

/**
 *
 * Invoked when existing asset associated are to be updated, delegating to the updateAsset
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const manageAsset = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: MANAGE_ADMIN_ASSET_REQUEST});
    try {
      const updated = await updateAsset(asset);
      dispatch({type: MANAGE_ADMIN_ASSET_RESPONSE, asset: updated});
    } catch (err) {
      dispatch({type: MANAGE_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Update Admin Asset Associations editor is to be displayed
 * or removed.
 */
export const updateAdminAsset = (asset: Asset, show = true) => ({
  type : UPDATE_ADMIN_ASSET, asset, show
});

/**
 *
 * Invoked when existing asset associated are to be updated, delegating to the
 * updateAssetToolAssociations CTC API and dispatching a
 *  successful response or error action accordingly. Replaces
 * the set of assets currently associated
 * with the asset, with the supplied set of assets.
 */
export const updateAssetAssociations = (asset: Asset) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: UPDATE_ADMIN_ASSET_REQUEST});
    try {
      const updated = await updateAssetToolAssociations(asset);
      dispatch({type: UPDATE_ADMIN_ASSET_RESPONSE, asset: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.asset)));
    } catch (err) {
      dispatch({type: UPDATE_ADMIN_ASSET_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.asset, err)));
      error(err);
    }
  };
};

/**
 *
 * Invoked when after a successful update of assets and/or operators
 * from the AssignmentEditor
 */
export const manageAssignmentsRefresh = () => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: UPDATE_ADMIN_ASSET_RESPONSE, asset: {}});
    dispatch(toggleUserPrompt(successEditOptions(Entity.asset)));
  };
};

/**
 *
 * Invoked after an unsuccessful attempt to update assets and/or operators
 * from the AssignmentEditor
 */
export const manageAssignmentsError = (err) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: UPDATE_ADMIN_ASSET_ERROR, error: err});
    dispatch(toggleUserPrompt(errorEditOptions(Entity.asset, err)));
    error(err);
  };
};

/**
 * Invoked when the Asset Bulk Edit Group Dialog is to be displayed or removed.
 */
export const showBulkEditGroupDialog = (assets: Asset[], show = true) => ({
  type : ASSET_BULK_EDIT_GROUP, assets, show
});

/**
 *
 * Invoked when the the asset auditor is to be displayed or removed.
 */
export const auditAdminAsset = (asset: Asset, show = true) => ({
  type : AUDIT_ADMIN_ASSET, asset, show
});

export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_ASSET_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 *
 * Invoked when the Delete Admin Asset Dialog is to be displayed or removed.
 */
export const deleteAdminAsset = (asset: Asset, show = true) => ({
  type : DELETE_ADMIN_ASSET, asset, show
});

/**
 *
 * Invoked when an existing asset is to be deleted, delegating to the deleteAsset
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteAsset = (asset: Asset, showTreeRefresh: boolean = false) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_ASSET_REQUEST});
    const {
      account,
      devices,
      id: assetId,
      schedules,
      maintenanceSchedules
    } = asset;
    const executions = [];
    if (maintenanceSchedules) {
      executions.unshift(deleteAssetMaintenanceSchedule(assetId));
    }
    if (devices?.length) {
      executions.unshift(disassociateDevices(asset));
    }
    try {
      if (schedules) {
        // delete schedule which is directly associated with the
        // asset before deleting it.
        await deleteAssetSchedule(asset);
      }
      const deleted = await destroyAsset(asset);
      await Promise.all(executions);
      dispatch({type: DELETE_ADMIN_ASSET_RESPONSE, asset: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_ASSET_ERROR, error: err});
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Bulk Edit Admin Asset editor is to be displayed or removed.
 */
export const editAdminAssets = (assets: Asset[], show = true) => ({
  type : EDIT_ADMIN_ASSETS, assets, show
});

/**
 *
 * Invoked when a collection of existing assets are to be updated, delegating to
 * the updateAssets CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const editAssets = (assets: Asset[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ASSETS_REQUEST});
    try {
      const {results} = await updateAssetSchedules(assets);
      dispatch(toggleUserPrompt(
        bulkSuccessEditOptions(
          translate('common:entityLabels.assets'),
          {results, items: assets})
      ));
      dispatch({type: EDIT_ADMIN_ASSETS_RESPONSE, assets: results});
    } catch (err) {
      dispatch({type: EDIT_ADMIN_ASSETS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Bulk Move Admin Asset editor is to be displayed or removed.
 */
export const moveAdminAssets = (assets: Asset[], show = true) => ({
  type : MOVE_ADMIN_ASSETS, assets, show
});

/**
 *
 * Invoked when a collection of existing assets are to be updated, delegating to
 * the updateAssets CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const moveAssets = (assets: Asset[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: MOVE_ADMIN_ASSETS_REQUEST});
    try {
      const {results, errors} = await updateAssetSchedules(assets);
      dispatch({type: MOVE_ADMIN_ASSETS_RESPONSE, assets: results, errors});
    } catch (err) {
      dispatch({type: MOVE_ADMIN_ASSETS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Moved assets grid is to be displayed or removed inside dialog.
 */
export const showMovedAssets = (show = true) => ({
  type : SHOW_MOVED_ASSETS, show
});

/**
 *
 * Invoked when the Delete Admin Assets Dialog is to be displayed or removed.
 */
export const deleteAdminAssets = (assets: Asset[], show = true) => ({
  type : DELETE_ADMIN_ASSETS, assets, show
});

/**
 *
 * Invoked when a collection of existing assets are to be updated, delegating to
 * the updateAssets CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteAssets = (assets: Asset[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_ASSETS_REQUEST});
    try {
      await deleteAssetDevices(assets);
      const deleted = await destroyAssets(assets, assets?.length);
      dispatch({type: DELETE_ADMIN_ASSETS_RESPONSE, assets: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_ASSETS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Called to update the modular search queries to the assets state
 */
export const updateAssetsSearchQueries = (searchParams: AssetsFilters) => ({
  type : UPDATE_ASSETS_SEARCH_QUERIES, searchParams
});
/**
 * Called to Reset the assets filters state
 */
export const resetAssetsSearchQueries = () => ({
  type : RESET_ASSETS_SEARCH_QUERIES
});

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setAssetViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the rows per page dropdown
 */
export const setAssetsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * REDUCERS
 */
export interface AdminAssetAction extends AdminAction {
  asset?: Asset;
  assets?: Asset[];
  isView: boolean;
  errors?: ServiceError[];
};

/**
 * Defines the shape of each admin asset edit state; i.e. add, edit, delete,
 * bulk edit, bulk delete.
 *
 * This type extends AsyncState which provides the loading and error states
 * associated with the action, and adds the following:
 *
 * - selected: Defines a reference to the asset or assets that are to be edited
 *   or deleted
 *
 * - selected: Defines a reference to the asset or assets that are to be edited
 *   or deleted
 * - result: Defines a reference to the service results after the selected item(s)
 *   have been updated or deleted.
 */
interface AdminAssetActionState extends AsyncState {
  selected: Asset | Asset[] | null;
  result: Asset | Asset[] | null;
  isEditing: boolean;
  isView: boolean;
  record?: Asset | Asset[] | null;
  viewMovedAssets?: boolean;
  errors?: ServiceError[];
};

export interface AdminAssetsAuditState extends AdminAssetActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
}
interface AdminAssetImportState {
  isImporting: boolean;
  result: Asset | Asset[] | null;
};

export interface AdminAssetsState extends PageState {
  assets: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  add: AdminAssetActionState,
  edit: AdminAssetActionState,
  manage: AdminAssetActionState,
  update: AdminAssetActionState,
  audit: AdminAssetsAuditState,
  destroy: AdminAssetActionState,
  bulkEdit: AdminAssetActionState,
  bulkDelete: AdminAssetActionState,
  bulkImport: AdminAssetImportState,
  move: AdminAssetActionState,
  search: AssetsFilters,
  bulkEditGroup: AdminAssetActionState
}

// defines the preference key for the admin assets page ...
export const PREFERENCE = 'admin-assets';

// defines the initial state for the reducer ...
export const initialState: AdminAssetsState = {
  isInitialized : false,
  assets        : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  manage : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  update : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  move : {
    isEditing       : false,
    selected        : null,
    loading         : false,
    error           : null,
    result          : null,
    isView          : false,
    errors          : null,
    viewMovedAssets : false
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null,
    isView    : false
  },
  bulkImport : {
    isImporting : false,
    result      : null
  },
  bulkEditGroup : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  search : {
    name          : null,
    status        : null,
    vin           : null,
    assetType     : null,
    licenseNumber : null,
    groups        : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_ASSETS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.destroy.error            = null;
  },
  [SET_GRID_COLUMNS] : ({grid, assets}: AdminAssetsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : assets.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminAssetsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_ASSETS_GRID_REFRESHED] : (
    {add, edit, manage, update, audit, destroy, bulkEdit, move, bulkDelete, bulkImport}: AdminAssetsState
  ) => {
    add.result = null;
    edit.result = null;
    manage.result = null;
    update.result = null;
    audit.result = null;
    destroy.result = null;
    bulkEdit.result = null;
    bulkDelete.result = null;
    bulkImport.result = null;
  },
  [GET_ADMIN_ASSETS_REQUEST] : ({assets}: AdminAssetsState) => {
    assets.loading = true;
  },
  [GET_ADMIN_ASSETS_RESPONSE] : ({assets}: AdminAssetsState) => {
    assets.loading = false;
  },
  [GET_ADMIN_ASSETS_ERROR] : ({assets}: AdminAssetsState, {error}: AdminAssetAction) => {
    assets.error = error;
    assets.loading = false;
  },

  [ADD_ADMIN_ASSET] : ({add}: AdminAssetsState, {show}: AdminAssetAction) => {
    add.isEditing = show;
    add.result = null;
  },
  [ADD_ADMIN_ASSET_REQUEST] : ({add}: AdminAssetsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_ASSET_RESPONSE] : ({add}: AdminAssetsState, {asset}: AdminAssetAction) => {
    add.loading = false;
    add.isEditing = false;
    add.result = asset;
  },
  [ADD_ADMIN_ASSET_ERROR] : ({add}: AdminAssetsState, {error}: AdminAssetAction) => {
    add.loading = false;
    add.error = error;
  },
  [IMPORT_ADMIN_ASSETS] : ({bulkImport}: AdminAssetsState, {show, result}: AdminAssetAction) => {
    bulkImport.isImporting = show;
    bulkImport.result = result;
  },
  [EDIT_ADMIN_ASSET] : ({edit}: AdminAssetsState, {asset, show, isView}: AdminAssetAction) => {
    edit.selected = show ? asset : null;
    edit.isEditing = show;
    edit.isView = isView;
    edit.result = null;
  },
  [EDIT_ADMIN_ASSET_REQUEST] : ({edit}: AdminAssetsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_ASSET_RESPONSE] : ({edit}: AdminAssetsState, {asset}: AdminAssetAction) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.result = asset;
  },
  [EDIT_ADMIN_ASSET_ERROR] : ({edit}: AdminAssetsState, {error}: AdminAssetAction) => {
    edit.loading = false;
    edit.error = error;
  },

  [MANAGE_ADMIN_ASSET] : ({manage}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    manage.selected = show ? asset : null;
    manage.isEditing = show;
    manage.result = null;
  },
  [MANAGE_ADMIN_ASSET_REQUEST] : ({manage}: AdminAssetsState) => {
    manage.loading = true;
    manage.error = manage.result = null;
  },
  [MANAGE_ADMIN_ASSET_RESPONSE] : ({manage}: AdminAssetsState, {asset}: AdminAssetAction) => {
    manage.loading = false;
    manage.isEditing = false;
    manage.result = asset;
  },
  [MANAGE_ADMIN_ASSET_ERROR] : ({manage}: AdminAssetsState, {error}: AdminAssetAction) => {
    manage.loading = false;
    manage.error = error;
  },

  [UPDATE_ADMIN_ASSET] : ({update}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    update.selected = show ? asset : null;
    update.isEditing = show;
    update.result = null;
  },
  [UPDATE_ADMIN_ASSET_REQUEST] : ({update}: AdminAssetsState) => {
    update.loading = true;
    update.error = update.result = null;
  },
  [UPDATE_ADMIN_ASSET_RESPONSE] : ({update}: AdminAssetsState, {asset}: AdminAssetAction) => {
    update.loading = false;
    update.isEditing = false;
    update.result = asset;
  },
  [UPDATE_ADMIN_ASSET_ERROR] : ({update}: AdminAssetsState, {error}: AdminAssetAction) => {
    update.loading = false;
    update.error = error;
  },
  [AUDIT_ADMIN_ASSET] : ({audit}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    audit.selected = show ? asset : null;
    audit.isEditing = show;
  },
  [AUDIT_ADMIN_ASSET_SHOW_CHANGES_DIALOG] : ({audit}: AdminAssetsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },
  [DELETE_ADMIN_ASSET] : ({destroy}: AdminAssetsState, {asset, show}: AdminAssetAction) => {
    destroy.selected = show ? asset : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_ASSET_REQUEST] : ({destroy}: AdminAssetsState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_ASSET_RESPONSE] : ({destroy}: AdminAssetsState, {asset}: AdminAssetAction) => {
    destroy.loading = false;
    destroy.isEditing = false;
    destroy.result = asset;
  },
  [DELETE_ADMIN_ASSET_ERROR] : ({destroy}: AdminAssetsState, {error}: AdminAssetAction) => {
    destroy.loading = false;
    destroy.error = error;
    destroy.result = null;
  },
  [EDIT_ADMIN_ASSETS] : ({bulkEdit}: AdminAssetsState, {assets, show}: AdminAssetAction) => {
    bulkEdit.selected = show ? assets : null;
    bulkEdit.isEditing = show;
    bulkEdit.result = null;
  },
  [EDIT_ADMIN_ASSETS_REQUEST] : ({bulkEdit}: AdminAssetsState) => {
    bulkEdit.loading = true;
    bulkEdit.error = bulkEdit.result = null;
  },
  [EDIT_ADMIN_ASSETS_RESPONSE] : ({bulkEdit}: AdminAssetsState, {assets}: AdminAssetAction) => {
    bulkEdit.loading = false;
    bulkEdit.isEditing = false;
    bulkEdit.result = assets;
  },
  [EDIT_ADMIN_ASSETS_ERROR] : ({bulkEdit}: AdminAssetsState, {error}: AdminAssetAction) => {
    bulkEdit.loading = false;
    bulkEdit.error = error;
  },
  [MOVE_ADMIN_ASSETS] : ({move}: AdminAssetsState, {assets, show}: AdminAssetAction) => {
    move.selected = show ? assets : null;
    move.isEditing = show;
    move.result = null;
  },
  [MOVE_ADMIN_ASSETS_REQUEST] : ({move}: AdminAssetsState) => {
    move.loading = true;
    move.error = move.result = null;
  },
  [MOVE_ADMIN_ASSETS_RESPONSE] : ({move}: AdminAssetsState, {assets, errors}: AdminAssetAction) => {
    move.loading = false;
    move.isEditing = false;
    move.result = assets;
    move.errors = errors;
  },
  [MOVE_ADMIN_ASSETS_ERROR] : ({move}: AdminAssetsState, {error}: AdminAssetAction) => {
    move.loading = false;
    move.error = error;
  },
  [SHOW_MOVED_ASSETS] : ({move}: AdminAssetsState, {show}: AdminAssetAction) => {
    move.viewMovedAssets = show;
  },

  [DELETE_ADMIN_ASSETS] : ({bulkDelete}: AdminAssetsState, {assets, show}: AdminAssetAction) => {
    bulkDelete.selected = show ? assets : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = bulkDelete.record = bulkDelete.error = null;
  },
  [DELETE_ADMIN_ASSETS_REQUEST] : ({bulkDelete}: AdminAssetsState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_ASSETS_RESPONSE] : ({bulkDelete}: AdminAssetsState, {assets}: AdminAssetAction) => {
    bulkDelete.loading = false;
    bulkDelete.isEditing = false;
    bulkDelete.result = assets;
    bulkDelete.record = assets;
  },
  [DELETE_ADMIN_ASSETS_ERROR] : ({bulkDelete}: AdminAssetsState, {error}: AdminAssetAction) => {
    bulkDelete.loading = false;
    bulkDelete.error = error;
  },
  [UPDATE_ASSETS_SEARCH_QUERIES] : ({search}: AdminAssetsState, {searchParams}) => {
    search.name = searchParams.name ?? null;
    search.status = searchParams.status ?? null;
    search.assetType = searchParams.assetType ?? null;
    search.licenseNumber = searchParams.licenseNumber ?? null;
    search.vin = searchParams.vin ?? null;
    search.groups = searchParams.groups ?? null;
  },
  [RESET_ASSETS_SEARCH_QUERIES] : ({search}: AdminAssetsState) => {
    search.name = null;
    search.status = null;
    search.assetType = null;
    search.licenseNumber = null;
    search.vin = null;
    search.groups = null;
  },
  [ASSET_BULK_EDIT_GROUP] : ({bulkEditGroup}: AdminAssetsState, {assets, show}: AdminAssetAction) => {
    bulkEditGroup.selected = show ? assets : null;
    bulkEditGroup.isEditing = show;
    bulkEditGroup.result = null;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: AdminAssetsState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminAssetsState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: PageState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
